// ── OrgRiskAcceptanceCreationPage ─────────────────────────────────────────────
//
// Route: /org-risk-acceptances/new  (create mode — no ?from=:id)
//        /org-risk-acceptances/new?from=:id  (edit mode — pre-filled from existing draft)
//
// Parallel to RiskAcceptanceCreationPage (D-ORA-ARCH-7 structural parallelism).
// ORA-specific differences:
//   - No vendor typeahead (org = current tenant; no vendor_id on ORA)
//   - Organization display from useAuthStore (not vendor dropdown)
//   - Uses ORA hooks + types
//   - acceptance_expires_at is REQUIRED (NOT NULL in V935)
//   - Navigation paths: /org-risk-acceptances/*
//   - Create via POST / (createAndSubmit) or POST /draft (createDraft) with mode field

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useAuthStore } from '@/store/auth.store';
import {
  useOrgRiskAcceptance,
  useCreateOrgRiskAcceptance,
  useUpdateDraftOrgRiskAcceptance,
  useSubmitOrgRiskAcceptance,
} from '@/api/hooks/useOrgRiskAcceptances';
// ⚠ O4b: reuse the vendor ResponseTypePicker (generic RiskResponseType) — the ORA create/edit pages gain
// the 5-type response-strategy picker + per-type conditional fields, mirroring RiskAcceptanceCreationPage.
import { ResponseTypePicker } from '@/components/risk-acceptances/ResponseTypePicker';
import type { RiskResponseType } from '@/api/types/risk-acceptance.types';

// Role guard hook (cluster pattern)
function useRequireRole(roles: string[]) {
  const navigate = useNavigate();
  const { isAuthenticated, isInitialized, hasAnyRole } = useAuthStore();
  useEffect(() => {
    if (isInitialized && isAuthenticated && !hasAnyRole(roles)) {
      navigate('/dashboard', { replace: true });
    }
  }, [isInitialized, isAuthenticated, hasAnyRole, navigate, roles]);
}

// ── Form field constants ───────────────────────────────────────────────────────

const RISK_SOURCES = [
  'vendor_assessment', 'compliance_assessment_finding', 'external_audit',
  'internal_audit', 'vuln_scan', 'pen_test', 'self_identified',
  'incident_derived', 'other',
] as const;

const RISK_SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;

const RISK_CATEGORIES = [
  'compliance_gap', 'vulnerability', 'operational_risk', 'other',
] as const;

// ── Date helper (same as vendor creation page — TD-RA.3b-6 pattern) ───────────

function toDateOnly(iso: string | null | undefined): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

// ── Component ─────────────────────────────────────────────────────────────────

export function OrgRiskAcceptanceCreationPage() {
  useEffect(() => { document.title = 'New Org Risk Response — VeriGRC'; }, []);
  useRequireRole(['risk_manager', 'super_admin']);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromId = searchParams.get('from');
  const isEditMode = !!fromId;

  const { user } = useAuthStore();
  // Organization display from auth context (no vendor lookup needed)
  const orgName = user?.tenantName ?? 'Your Organization';

  // Pre-fill from existing entry in edit mode
  const { data: existingEntry, isLoading: entryLoading } = useOrgRiskAcceptance(fromId ?? undefined);

  // Form state — native useState per cluster pattern
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [riskCategory, setRiskCategory] = useState('');
  const [source, setSource] = useState('');
  const [sourceOtherSpecify, setSourceOtherSpecify] = useState('');
  const [potentialImpact, setPotentialImpact] = useState('');
  const [likelihood, setLikelihood] = useState('');
  const [impactSeverity, setImpactSeverity] = useState('');
  const [inherentRiskRating, setInherentRiskRating] = useState('');
  const [acceptanceStartDate, setAcceptanceStartDate] = useState('');
  const [acceptanceExpiresAt, setAcceptanceExpiresAt] = useState('');
  const [businessJustification, setBusinessJustification] = useState('');
  const [categoryOtherSpecify, setCategoryOtherSpecify] = useState('');
  const [inherentRiskOverride, setInherentRiskOverride] = useState(false);
  const [inherentRiskOverrideJustification, setInherentRiskOverrideJustification] = useState('');
  const [compensatingControls, setCompensatingControls] = useState('');
  // ⚠ O4b response-type axis (mirrors vendor). residual_risk_rating is no longer a standalone field — for
  // Mitigate the residual is reassessedResidualRating (the server backfills residual_risk_rating from it to
  // satisfy chk_org_ra_compensating_residual_required). Per-type state in distinct vars → preserved across
  // type switches.
  const [plannedResponseType, setPlannedResponseType] = useState<RiskResponseType>('accept');
  const [mitigateStage, setMitigateStage] = useState<'planned' | 'implemented'>('planned');
  const [responseRationale, setResponseRationale] = useState('');
  const [discontinuedScope, setDiscontinuedScope] = useState('');
  const [responsibleThirdParty, setResponsibleThirdParty] = useState('');
  const [reassessedResidualRating, setReassessedResidualRating] = useState('');
  const [longDurationJustification, setLongDurationJustification] = useState('');
  const [internalStakeholders, setInternalStakeholders] = useState('');
  const [externalEvidenceReferences, setExternalEvidenceReferences] = useState('');

  // fieldErrors state (D-RA.3b-PE-4 cluster pattern)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Submit warning modal state
  const [showSubmitWarning, setShowSubmitWarning] = useState(false);
  const [_pendingSubmitMode, setPendingSubmitMode] = useState<'draft' | 'submit'>('draft');

  // Mutations
  const createMutation = useCreateOrgRiskAcceptance();
  const updateMutation = useUpdateDraftOrgRiskAcceptance();
  const submitMutation = useSubmitOrgRiskAcceptance();

  const isPending = createMutation.isPending || updateMutation.isPending || submitMutation.isPending;

  // Pre-fill from existing entry (edit mode)
  useEffect(() => {
    if (existingEntry && isEditMode) {
      setTitle(existingEntry.title ?? '');
      setDescription(existingEntry.description ?? '');
      setRiskCategory(existingEntry.risk_category ?? '');
      setSource(existingEntry.source ?? '');
      setSourceOtherSpecify(existingEntry.source_other_specify ?? '');
      setPotentialImpact(existingEntry.potential_impact ?? '');
      setLikelihood(existingEntry.likelihood ?? '');
      setImpactSeverity(existingEntry.impact_severity ?? '');
      setInherentRiskRating(existingEntry.inherent_risk_rating ?? '');
      setAcceptanceStartDate(toDateOnly(existingEntry.acceptance_start_date));
      setAcceptanceExpiresAt(toDateOnly(existingEntry.acceptance_expires_at));
      setBusinessJustification(existingEntry.business_justification ?? '');
      setCategoryOtherSpecify(existingEntry.category_other_specify ?? '');
      setInherentRiskOverride(existingEntry.inherent_risk_override ?? false);
      setInherentRiskOverrideJustification(existingEntry.inherent_risk_override_justification ?? '');
      setCompensatingControls(existingEntry.compensating_controls ?? '');
      setPlannedResponseType((existingEntry.planned_response_type ?? 'accept') as RiskResponseType);
      setMitigateStage(existingEntry.implemented_response_type != null || existingEntry.response_implemented_at != null ? 'implemented' : 'planned');
      setResponseRationale(existingEntry.response_rationale ?? '');
      setDiscontinuedScope(existingEntry.discontinued_scope ?? '');
      setResponsibleThirdParty(existingEntry.responsible_third_party ?? '');
      setReassessedResidualRating(existingEntry.reassessed_residual_rating ?? '');
      setLongDurationJustification(existingEntry.long_duration_justification ?? '');
      setInternalStakeholders(existingEntry.internal_stakeholders ?? '');
      setExternalEvidenceReferences(existingEntry.external_evidence_references ?? '');
    }
  }, [existingEntry, isEditMode]);

  // §10.8 — conditional field rendering
  const showCategoryOther = riskCategory === 'other';
  const showSourceOther = source === 'other';
  const showOverrideJustification = inherentRiskOverride;

  // ⚠ O4b — the per-(type×phase) required-field matrix (mirrors the server O2b validator, the source of truth).
  // Accept → rationale + expiry; Avoid → discontinued scope; Share/Transfer → responsible third party;
  // Mitigate-IMPLEMENTED → compensating controls + reassessed residual (planned Mitigate requires nothing).
  const responseFieldsSatisfied =
    (plannedResponseType !== 'accept' || (responseRationale.trim().length > 0 && acceptanceExpiresAt !== '')) &&
    (plannedResponseType !== 'avoid' || discontinuedScope.trim().length > 0) &&
    ((plannedResponseType !== 'share' && plannedResponseType !== 'transfer') || responsibleThirdParty.trim().length > 0) &&
    (plannedResponseType !== 'mitigate' || mitigateStage !== 'implemented' ||
      (compensatingControls.trim().length > 0 && reassessedResidualRating !== ''));

  // canSubmit — ⚠ RR-BI-1 Slice 2b item-6b: acceptance_expires_at is Accept-CONDITIONAL (nullable since
  // O1/V995 + Accept-conditional CHECK), NOT unconditionally required. The Accept case is enforced above in
  // responseFieldsSatisfied (Accept → rationale + expiry); non-Accept may leave it blank ("No expiry").
  const canSubmit =
    title.trim().length >= 10 &&
    description.trim().length >= 50 &&
    riskCategory !== '' &&
    source !== '' &&
    (!showSourceOther || sourceOtherSpecify.trim().length > 0) &&
    (!showCategoryOther || categoryOtherSpecify.trim().length > 0) &&
    potentialImpact.trim().length >= 30 &&
    likelihood !== '' &&
    impactSeverity !== '' &&
    inherentRiskRating !== '' &&
    acceptanceStartDate !== '' &&
    businessJustification.trim().length >= 100 &&
    (!showOverrideJustification || inherentRiskOverrideJustification.trim().length > 0) &&
    responseFieldsSatisfied;

  // §6.3 — error envelope detection
  function handleApiError(err: unknown): void {
    const axiosErr = err as { response?: { status?: number; data?: { errors?: unknown } } };
    const status = axiosErr?.response?.status;
    const errData = axiosErr?.response?.data?.errors;

    if (status === 400 && errData && !Array.isArray(errData) && typeof errData === 'object') {
      const fe = errData as Record<string, string[]>;
      const mapped: Record<string, string> = {};
      for (const [key, msgs] of Object.entries(fe)) {
        if (Array.isArray(msgs) && msgs.length > 0) mapped[key] = msgs[0];
      }
      setFieldErrors(mapped);
    } else if (status === 400 && Array.isArray(errData)) {
      toast.error((errData as Array<{ message: string }>)[0]?.message ?? 'Validation error');
    } else if (status === 403) {
      toast.error("You don't have permission for this action");
    } else {
      toast.error('Something went wrong. Please try again.');
    }
  }

  function buildPayload() {
    return {
      title, description, risk_category: riskCategory, source,
      source_other_specify: showSourceOther ? sourceOtherSpecify : null,
      potential_impact: potentialImpact, likelihood, impact_severity: impactSeverity,
      inherent_risk_rating: inherentRiskRating, acceptance_start_date: acceptanceStartDate,
      // ⚠ item-6b: send null (not '') when blank — Accept-conditional; the server schema is .date().nullable().
      acceptance_expires_at: acceptanceExpiresAt || null,
      business_justification: businessJustification,
      category_other_specify: showCategoryOther ? categoryOtherSpecify : null,
      inherent_risk_override: inherentRiskOverride,
      inherent_risk_override_justification: showOverrideJustification ? inherentRiskOverrideJustification : null,
      // ⚠ O4b response-type axis — only the ACTIVE type's fields are sent (others null). implemented_
      // response_type = the planned type once Mitigate is Implemented; residual_risk_rating is NOT sent
      // directly — the server backfills it from reassessed_residual_rating (D-RR-RESIDUAL-BACKFILL).
      planned_response_type: plannedResponseType,
      implemented_response_type:
        plannedResponseType === 'mitigate' && mitigateStage === 'implemented'
          ? ('mitigate' as RiskResponseType)
          : null,
      response_rationale: plannedResponseType === 'accept' ? (responseRationale || null) : null,
      discontinued_scope: plannedResponseType === 'avoid' ? (discontinuedScope || null) : null,
      responsible_third_party:
        plannedResponseType === 'share' || plannedResponseType === 'transfer'
          ? (responsibleThirdParty || null)
          : null,
      compensating_controls: plannedResponseType === 'mitigate' ? (compensatingControls || null) : null,
      reassessed_residual_rating: plannedResponseType === 'mitigate' ? (reassessedResidualRating || null) : null,
      long_duration_justification: longDurationJustification || null,
      internal_stakeholders: internalStakeholders || null,
      external_evidence_references: externalEvidenceReferences || null,
    };
  }

  async function handleSaveAsDraft() {
    setFieldErrors({});
    try {
      if (isEditMode && fromId) {
        const entry = await updateMutation.mutateAsync({ id: fromId, payload: buildPayload() });
        toast.success('Draft saved');
        navigate(`/org-risk-acceptances/${entry.id}`);
      } else {
        const { data: entry } = await createMutation.mutateAsync({ mode: 'draft', ...buildPayload() });
        toast.success('Draft saved');
        navigate(`/org-risk-acceptances/${entry.id}`);
      }
    } catch (err) {
      handleApiError(err);
    }
  }

  async function handleSubmitForApproval() {
    setFieldErrors({});
    try {
      if (isEditMode && fromId) {
        // Save changes first, then submit
        await updateMutation.mutateAsync({ id: fromId, payload: buildPayload() });
        const entry = await submitMutation.mutateAsync(fromId);
        toast.success('Submitted for approval');
        navigate(`/org-risk-acceptances/${entry.id}`);
      } else {
        // Create and submit in one request
        const { data: entry } = await createMutation.mutateAsync({ mode: 'submit', ...buildPayload() });
        toast.success('Submitted for approval');
        navigate(`/org-risk-acceptances/${entry.id}`);
      }
    } catch (err) {
      handleApiError(err);
    }
  }

  if (isEditMode && entryLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 animate-pulse rounded bg-bg-surface-2" />
        <div className="h-64 animate-pulse rounded bg-bg-surface-2" />
      </div>
    );
  }

  const pageTitle = isEditMode ? 'Edit Org Risk Response Draft' : 'Create Organizational Risk Response';

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">{pageTitle}</h1>
        <p className="mt-1 text-sm text-text-secondary">
          {isEditMode
            ? 'Update your draft before saving or submitting for approval.'
            : 'Submit a new organizational risk response for Approver review.'}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Risk Response Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">

          {/* Organization display (no vendor lookup; org = current tenant) */}
          <div className="space-y-1.5">
            <Label htmlFor="organization">Organization</Label>
            <Input
              id="organization"
              value={orgName}
              disabled
              className="opacity-60"
            />
            <p className="text-xs text-text-secondary">Organizational risk responses are always scoped to your organization.</p>
          </div>

          {/* Title */}
          <div className="space-y-1.5">
            <Label htmlFor="title">Title <span className="text-accent-rose">*</span></Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief descriptive title (10–200 chars)"
              maxLength={200}
            />
            <p className="text-xs text-text-secondary">at least 10 characters ({title.length}/200)</p>
            {fieldErrors.title && <p className="text-xs text-accent-rose">{fieldErrors.title}</p>}
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="description">Description <span className="text-accent-rose">*</span></Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="Detailed description of the risk (at least 50 characters)"
            />
            <p className="text-xs text-text-secondary">
              {description.trim().length < 50
                ? `at least 50 characters (${description.trim().length}/50)`
                : `${description.trim().length}/2000`}
            </p>
            {fieldErrors.description && <p className="text-xs text-accent-rose">{fieldErrors.description}</p>}
          </div>

          {/* Risk Category */}
          <div className="space-y-1.5">
            <Label htmlFor="risk-category">Risk Category <span className="text-accent-rose">*</span></Label>
            <select
              id="risk-category"
              value={riskCategory}
              onChange={(e) => { setRiskCategory(e.target.value); setCategoryOtherSpecify(''); }}
              className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan"
            >
              <option value="">Select category…</option>
              {RISK_CATEGORIES.map((c) => (
                <option key={c} value={c}>{c.replace(/_/g, ' ').replace(/\b\w/g, (s) => s.toUpperCase())}</option>
              ))}
            </select>
            {fieldErrors.risk_category && <p className="text-xs text-accent-rose">{fieldErrors.risk_category}</p>}
          </div>

          {showCategoryOther && (
            <div className="space-y-1.5">
              <Label htmlFor="category-other">Category — Other (specify) <span className="text-accent-rose">*</span></Label>
              <Input
                id="category-other"
                value={categoryOtherSpecify}
                onChange={(e) => setCategoryOtherSpecify(e.target.value)}
                placeholder="Describe the risk category"
              />
              {fieldErrors.category_other_specify && <p className="text-xs text-accent-rose">{fieldErrors.category_other_specify}</p>}
            </div>
          )}

          {/* Source */}
          <div className="space-y-1.5">
            <Label htmlFor="source">Risk Source <span className="text-accent-rose">*</span></Label>
            <select
              id="source"
              value={source}
              onChange={(e) => { setSource(e.target.value); setSourceOtherSpecify(''); }}
              className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan"
            >
              <option value="">Select source…</option>
              {RISK_SOURCES.map((s) => (
                <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</option>
              ))}
            </select>
            {fieldErrors.source && <p className="text-xs text-accent-rose">{fieldErrors.source}</p>}
          </div>

          {showSourceOther && (
            <div className="space-y-1.5">
              <Label htmlFor="source-other">Source — Other (specify) <span className="text-accent-rose">*</span></Label>
              <Input
                id="source-other"
                value={sourceOtherSpecify}
                onChange={(e) => setSourceOtherSpecify(e.target.value)}
                placeholder="Describe the risk source"
              />
              {fieldErrors.source_other_specify && <p className="text-xs text-accent-rose">{fieldErrors.source_other_specify}</p>}
            </div>
          )}

          {/* Potential Impact */}
          <div className="space-y-1.5">
            <Label htmlFor="potential-impact">Potential Impact <span className="text-accent-rose">*</span></Label>
            <Textarea
              id="potential-impact"
              value={potentialImpact}
              onChange={(e) => setPotentialImpact(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Describe the potential business impact (at least 30 characters)"
            />
            <p className="text-xs text-text-secondary">
              {potentialImpact.trim().length < 30
                ? `at least 30 characters (${potentialImpact.trim().length}/30)`
                : `${potentialImpact.trim().length}/1000`}
            </p>
            {fieldErrors.potential_impact && <p className="text-xs text-accent-rose">{fieldErrors.potential_impact}</p>}
          </div>

          {/* Severity fields */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="likelihood">Likelihood <span className="text-accent-rose">*</span></Label>
              <select
                id="likelihood"
                value={likelihood}
                onChange={(e) => setLikelihood(e.target.value)}
                className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan"
              >
                <option value="">—</option>
                {RISK_SEVERITIES.map((s) => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
              {fieldErrors.likelihood && <p className="text-xs text-accent-rose">{fieldErrors.likelihood}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="impact-severity">Impact Severity <span className="text-accent-rose">*</span></Label>
              <select
                id="impact-severity"
                value={impactSeverity}
                onChange={(e) => setImpactSeverity(e.target.value)}
                className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan"
              >
                <option value="">—</option>
                {RISK_SEVERITIES.map((s) => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
              {fieldErrors.impact_severity && <p className="text-xs text-accent-rose">{fieldErrors.impact_severity}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inherent-risk-rating">Inherent Risk Rating <span className="text-accent-rose">*</span></Label>
              <select
                id="inherent-risk-rating"
                value={inherentRiskRating}
                onChange={(e) => setInherentRiskRating(e.target.value)}
                className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan"
              >
                <option value="">—</option>
                {RISK_SEVERITIES.map((s) => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
              {fieldErrors.inherent_risk_rating && <p className="text-xs text-accent-rose">{fieldErrors.inherent_risk_rating}</p>}
            </div>
          </div>

          {/* Inherent Risk Override */}
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm text-text-primary cursor-pointer">
              <input
                type="checkbox"
                checked={inherentRiskOverride}
                onChange={(e) => { setInherentRiskOverride(e.target.checked); setInherentRiskOverrideJustification(''); }}
                className="h-4 w-4 rounded border-border-subtle"
              />
              Override inherent risk rating (requires justification)
            </label>
          </div>

          {showOverrideJustification && (
            <div className="space-y-1.5">
              <Label htmlFor="override-justification">Override Justification <span className="text-accent-rose">*</span></Label>
              <Textarea
                id="override-justification"
                value={inherentRiskOverrideJustification}
                onChange={(e) => setInherentRiskOverrideJustification(e.target.value)}
                rows={3}
                placeholder="Explain why the inherent risk rating is being overridden"
              />
              {fieldErrors.inherent_risk_override_justification && (
                <p className="text-xs text-accent-rose">{fieldErrors.inherent_risk_override_justification}</p>
              )}
            </div>
          )}

          {/* ── Response strategy (O4b) — the picker determines which fields are required ── */}
          <div className="space-y-3 rounded-lg border border-border-subtle bg-bg-surface/40 p-4">
            <div className="space-y-1">
              <Label>Response Strategy <span className="text-accent-rose">*</span></Label>
              <p className="text-xs text-text-secondary">How this risk is being handled — the choice determines which fields become required.</p>
            </div>
            <ResponseTypePicker value={plannedResponseType} onChange={setPlannedResponseType} />

            {/* Accept → rationale (+ expiry, required below in Dates) */}
            {plannedResponseType === 'accept' && (
              <div className="space-y-1.5">
                <Label htmlFor="response-rationale">Rationale for accepting <span className="text-accent-rose">*</span></Label>
                <Textarea
                  id="response-rationale"
                  value={responseRationale}
                  onChange={(e) => setResponseRationale(e.target.value)}
                  rows={3}
                  placeholder="Why the residual exposure is tolerable, and what was weighed against it."
                />
                <p className="text-xs text-text-secondary">Accept also requires an expiry date (in Dates, below).</p>
                {fieldErrors.response_rationale && <p className="text-xs text-accent-rose">{fieldErrors.response_rationale}</p>}
              </div>
            )}

            {/* Avoid → discontinued scope */}
            {plannedResponseType === 'avoid' && (
              <div className="space-y-1.5">
                <Label htmlFor="discontinued-scope">Scope being discontinued <span className="text-accent-rose">*</span></Label>
                <Textarea
                  id="discontinued-scope"
                  value={discontinuedScope}
                  onChange={(e) => setDiscontinuedScope(e.target.value)}
                  rows={3}
                  placeholder="Which service, integration or data flow stops — precisely enough that someone can verify it later."
                />
                <p className="text-xs text-text-secondary">Avoid stays open until the discontinuation is confirmed — recording the intent is not the same as the scope being gone.</p>
                {fieldErrors.discontinued_scope && <p className="text-xs text-accent-rose">{fieldErrors.discontinued_scope}</p>}
              </div>
            )}

            {/* Share / Transfer → one shared block (only the note differs) */}
            {(plannedResponseType === 'share' || plannedResponseType === 'transfer') && (
              <div className="space-y-1.5">
                <Label htmlFor="responsible-third-party">Responsible third party <span className="text-accent-rose">*</span></Label>
                <Input
                  id="responsible-third-party"
                  value={responsibleThirdParty}
                  onChange={(e) => setResponsibleThirdParty(e.target.value)}
                  placeholder="Name who carries the risk"
                />
                <p className="text-xs text-text-secondary">
                  {plannedResponseType === 'share'
                    ? 'Share — the risk is carried jointly with the named third party.'
                    : 'Transfer — the risk moves wholly to the named third party.'}
                </p>
                {fieldErrors.responsible_third_party && <p className="text-xs text-accent-rose">{fieldErrors.responsible_third_party}</p>}
              </div>
            )}

            {/* Mitigate → stage + compensating controls + reassessed residual */}
            {plannedResponseType === 'mitigate' && (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Stage of this mitigation</Label>
                  <div className="flex flex-wrap gap-2">
                    {(['planned', 'implemented'] as const).map((st) => (
                      <button
                        key={st}
                        type="button"
                        onClick={() => setMitigateStage(st)}
                        className={`rounded-md border px-3 py-1.5 text-sm transition-colors ${
                          mitigateStage === st
                            ? 'border-accent-cyan bg-accent-cyan-dim/15 text-text-primary'
                            : 'border-border-subtle text-text-secondary hover:border-accent-cyan/40'
                        }`}
                      >
                        {st === 'planned' ? 'Planned' : 'Implemented'}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-text-secondary">
                    An implemented mitigation stays <strong className="text-text-primary">active</strong> — applying a compensating control is an implementation event, not a closure.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="compensating-controls">
                    Compensating controls {mitigateStage === 'implemented' && <span className="text-accent-rose">*</span>}
                  </Label>
                  <Textarea
                    id="compensating-controls"
                    value={compensatingControls}
                    onChange={(e) => setCompensatingControls(e.target.value)}
                    rows={3}
                    placeholder="What is being put in place, and how its effect will be evidenced."
                  />
                  {fieldErrors.compensating_controls && <p className="text-xs text-accent-rose">{fieldErrors.compensating_controls}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reassessed-residual">
                    Residual rating after the controls {mitigateStage === 'implemented'
                      ? <span className="text-accent-rose">*</span>
                      : <span className="text-xs font-normal text-text-secondary">(optional while planned)</span>}
                  </Label>
                  <select
                    id="reassessed-residual"
                    value={reassessedResidualRating}
                    onChange={(e) => setReassessedResidualRating(e.target.value)}
                    className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan"
                  >
                    <option value="">—</option>
                    {RISK_SEVERITIES.map((s) => (
                      <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                    ))}
                  </select>
                  {fieldErrors.reassessed_residual_rating && <p className="text-xs text-accent-rose">{fieldErrors.reassessed_residual_rating}</p>}
                </div>
              </div>
            )}
          </div>

          {/* Dates — acceptance_expires_at REQUIRED for ORA */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="start-date">Start Date <span className="text-accent-rose">*</span></Label>
              <Input
                id="start-date"
                type="date"
                value={acceptanceStartDate}
                onChange={(e) => setAcceptanceStartDate(e.target.value)}
              />
              {fieldErrors.acceptance_start_date && <p className="text-xs text-accent-rose">{fieldErrors.acceptance_start_date}</p>}
            </div>
            <div className="space-y-1.5">
              {/* ⚠ item-6b: required only for Accept; optional ("No expiry") otherwise. */}
              <Label htmlFor="expires-at">
                Expiry Date{' '}
                {plannedResponseType === 'accept'
                  ? <span className="text-accent-rose">*</span>
                  : <span className="text-xs font-normal text-text-secondary">(optional — leave blank for no expiry)</span>}
              </Label>
              <Input
                id="expires-at"
                type="date"
                value={acceptanceExpiresAt}
                onChange={(e) => setAcceptanceExpiresAt(e.target.value)}
              />
              {fieldErrors.acceptance_expires_at && <p className="text-xs text-accent-rose">{fieldErrors.acceptance_expires_at}</p>}
            </div>
          </div>

          {/* Business Justification */}
          <div className="space-y-1.5">
            <Label htmlFor="business-justification">Business Justification <span className="text-accent-rose">*</span></Label>
            <Textarea
              id="business-justification"
              value={businessJustification}
              onChange={(e) => setBusinessJustification(e.target.value)}
              rows={5}
              maxLength={3000}
              placeholder="Detailed justification for accepting this risk (at least 100 characters)"
            />
            <p className="text-xs text-text-secondary">
              {businessJustification.trim().length < 100
                ? `at least 100 characters (${businessJustification.trim().length}/100)`
                : `${businessJustification.trim().length}/3000`}
            </p>
            {fieldErrors.business_justification && <p className="text-xs text-accent-rose">{fieldErrors.business_justification}</p>}
          </div>

          {/* Optional fields */}
          <div className="space-y-1.5">
            <Label htmlFor="long-duration">Long Duration Justification</Label>
            <Textarea
              id="long-duration"
              value={longDurationJustification}
              onChange={(e) => setLongDurationJustification(e.target.value)}
              rows={2}
              placeholder="If the response period exceeds 12 months, explain why (optional)"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="stakeholders">Internal Stakeholders</Label>
            <Input
              id="stakeholders"
              value={internalStakeholders}
              onChange={(e) => setInternalStakeholders(e.target.value)}
              placeholder="Relevant internal stakeholders (optional)"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="evidence-refs">External Evidence References</Label>
            <Textarea
              id="evidence-refs"
              value={externalEvidenceReferences}
              onChange={(e) => setExternalEvidenceReferences(e.target.value)}
              rows={2}
              placeholder="Links or references to supporting evidence (optional)"
            />
          </div>

        </CardContent>
      </Card>

      {/* Action buttons */}
      <div className="flex items-center justify-end gap-3">
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          variant="outline"
          onClick={handleSaveAsDraft}
          disabled={!canSubmit || isPending}
        >
          {(createMutation.isPending || updateMutation.isPending) && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          Save as Draft
        </Button>
        <Button
          onClick={() => {
            setPendingSubmitMode('submit');
            setShowSubmitWarning(true);
          }}
          disabled={!canSubmit || isPending}
        >
          {submitMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Submit for Approval
        </Button>
      </div>

      {/* Submit warning modal */}
      {showSubmitWarning && (
        <Dialog open onOpenChange={(open) => { if (!open) setShowSubmitWarning(false); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-accent-amber" />
                Submit for Approval
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-text-secondary">
              You are submitting for approval. Once submitted, the entry cannot be edited
              until an Approver returns it for revision.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSubmitWarning(false)} disabled={isPending}>
                Go Back
              </Button>
              <Button
                onClick={async () => {
                  setShowSubmitWarning(false);
                  await handleSubmitForApproval();
                }}
                disabled={isPending}
              >
                {submitMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Confirm Submission
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
